// Three named feels for the playback engine.
// Each one is a complete set of timing + velocity constants.

export const PRESETS = {
  // The house feel for every demo of the tool: one letter every ~0.95s, each
  // tone ringing ~3.9s, so roughly four notes are overlapping and blooming at
  // any moment. Called with the pad voice and pedal off.
  // The aleph-bet specimen on the homepage: an even half-second a letter, so
  // you hear the scale climb rather than a rubato phrase.
  alefbet: {
    label: "Aleph-bet",
    blurb: "Even, half a second a letter.",
    baseStepMs: 500,
    stepJitterMs: 0,
    firstLetterMult: 1,
    secondToLastMult: 1,
    lastLetterMult: 1.6,
    wordRestMs: 0,
    graceChance: 0,
    graceShortMult: 1,
    graceNextMult: 1,
    velFirst: [0.52, 0.58],
    velLast: [0.52, 0.6],
    velBodyMin: 0.46,
    velBodyRange: 0.08,
    interiorTailSec: 1.6,
    finalTailSec: 3.0,
  },
  meditation: {
    label: "Meditative",
    blurb: "Ambient — a letter every 0.7s, each one blooming.",
    voice: "pad",
    // One letter per beat: 700ms ≈ 86 BPM.
    defaultBpm: 86,
    // 100 = the tails and room this feel was voiced with.
    defaultSustain: 100,
    stepBeats: 1,
    baseStepMs: 700,
    stepJitterMs: 60,
    firstLetterMult: 1.05,
    secondToLastMult: 1.15,
    lastLetterMult: 1.8,
    wordRestMs: 750,
    graceChance: 0,
    graceShortMult: 1,
    graceNextMult: 1,
    velFirst: [0.5, 0.62],
    velLast: [0.55, 0.68],
    velBodyMin: 0.42,
    velBodyRange: 0.12,
    interiorTailSec: 3.0,
    finalTailSec: 5.0,
  },
  // Piano with a swung eighth feel: long-short, long-short. `swing` is the
  // share of the beat the first note takes (0.5 = straight, 0.62 ≈ triplet).
  groove: {
    label: "Groove",
    blurb: "Steinway, swung — it walks instead of floating.",
    voice: "piano",
    swing: 0.62,
    // Letters land on eighths here, so 300ms ≈ 100 BPM.
    defaultBpm: 100,
    defaultSustain: 100,
    stepBeats: 0.5,
    baseStepMs: 300,
    stepJitterMs: 18,
    firstLetterMult: 1,
    secondToLastMult: 1.05,
    lastLetterMult: 1.5,
    wordRestMs: 260,
    // No grace notes here — the swing carries the feel on its own.
    graceChance: 0,
    graceShortMult: 1,
    graceNextMult: 1,
    velFirst: [0.72, 0.9],
    velLast: [0.7, 0.9],
    velBodyMin: 0.52,
    velBodyRange: 0.2,
    interiorTailSec: 0.55,
    finalTailSec: 1.6,
    pedalTailMult: 1.4,
  },
  telegraph: {
    label: "Telegraph",
    blurb: "Brisk, steady, almost in tempo.",
    baseStepMs: 150,
    stepJitterMs: 60,
    firstLetterMult: 1.0,
    secondToLastMult: 1.1,
    lastLetterMult: 1.7,
    wordRestMs: 60,
    graceChance: 0.05,
    graceShortMult: 0.5,
    graceNextMult: 1.15,
    velFirst: [0.7, 0.85],
    velLast: [0.7, 0.88],
    velBodyMin: 0.55,
    velBodyRange: 0.18,
    interiorTailSec: 0.35,
    finalTailSec: 0.6,
  },
  lamplight: {
    label: "Lamplight",
    blurb: "ECM phrasing — breathing rubato.",
    baseStepMs: 200,
    stepJitterMs: 120,
    firstLetterMult: 1.25,
    secondToLastMult: 1.45,
    lastLetterMult: 3.0,
    wordRestMs: 90,
    graceChance: 0.18,
    graceShortMult: 0.42,
    graceNextMult: 1.35,
    velFirst: [0.78, 0.92],
    velLast: [0.82, 0.95],
    velBodyMin: 0.45,
    velBodyRange: 0.22,
    interiorTailSec: 0.5,
    finalTailSec: 1.1,
  },
  inkwell: {
    label: "Inkwell",
    blurb: "Slow, sustained, swimming in rubato.",
    baseStepMs: 260,
    stepJitterMs: 180,
    firstLetterMult: 1.4,
    secondToLastMult: 1.7,
    lastLetterMult: 4.2,
    wordRestMs: 130,
    graceChance: 0.28,
    graceShortMult: 0.38,
    graceNextMult: 1.55,
    velFirst: [0.72, 0.88],
    velLast: [0.85, 0.97],
    velBodyMin: 0.4,
    velBodyRange: 0.25,
    interiorTailSec: 0.8,
    finalTailSec: 1.8,
  },
};

export const PEDAL_TAIL_MULTIPLIER = 4.5;

// The two playback modes the studio exposes.
export const PLAYBACK_MODES = [
  { value: "meditation", label: "Meditative" },
  { value: "groove", label: "Groove" },
];

export const BPM_MIN = 40;
export const BPM_MAX = 180;

export function defaultBpmFor(presetKey) {
  const p = PRESETS[presetKey];
  if (p?.defaultBpm) return p.defaultBpm;
  // Derive one from the preset's own step if it never declared a BPM.
  const beats = p?.stepBeats ?? 1;
  return p?.baseStepMs ? Math.round((60000 * beats) / p.baseStepMs) : 86;
}

export function defaultSustainFor(presetKey) {
  return PRESETS[presetKey]?.defaultSustain ?? 100;
}

// One knob, because sustain is what actually decides whether notes pile up:
// it scales the note tails directly and takes the room with it at half
// strength, so the space never disappears completely.
export function reverbMixForSustain(sustainPct) {
  const pct = Math.min(200, Math.max(0, Number(sustainPct) || 0));
  return 0.5 + 0.5 * (pct / 100);
}

// A letter is `stepBeats` of a beat, so the gap between letters follows the BPM.
export function stepMsForBpm(presetKey, bpm) {
  const p = PRESETS[presetKey];
  const beats = p?.stepBeats ?? 1;
  const safe = Math.min(BPM_MAX, Math.max(BPM_MIN, Number(bpm) || defaultBpmFor(presetKey)));
  return (60000 * beats) / safe;
}

export function voiceForPreset(presetKey) {
  return PRESETS[presetKey]?.voice || "pad";
}
